import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import DashboardLayout from '../components/DashboardLayout';
import EmployeeFilter from '../components/EmployeeFilter';
import { useAppSelector, useAppDispatch } from '../store';
import { useEmployeeRecords } from '../hooks/useEmployeeRecords';
import { apiService } from '../services/api';
import { showNotification } from '../store/notificationSlice';
import type { TimekeepingErrorRow, TimekeepingErrorEntry } from '../types/timekeeping';

// ── Colour tokens ──────────────────────────────────────────────────────────────
const COLOR = {
  activeMachine:      { bg: '#43a047', border: '#2e7d32', text: '#ffffff' },
  deactivatedMachine: { bg: '#9e9e9e', border: '#757575', text: '#ffffff' },
  existingManual:     { bg: '#e53935', border: '#b71c1c', text: '#ffffff' },
  // Pending changes
  pendingAdd:         { bg: '#fff8e1', border: '#f9a825', text: '#e65100' },
  pendingRemove:      { bg: '#fbe9e7', border: '#ff5722', text: '#bf360c' },
  pendingReactivate:  { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' },
} as const;

type EntryState = keyof typeof COLOR;

interface DisplayEntry extends TimekeepingErrorEntry {
  state: EntryState;
}

interface LocalRow extends TimekeepingErrorRow {
  entries: DisplayEntry[];
  pendingAdd?: string;        // RFC3339 timestamp
  pendingRemove?: string;     // timestamp of removed entry
  pendingReactivate?: string; // timestamp of reactivated entry
  isDirty?: boolean;
}

function getEntryState(e: TimekeepingErrorEntry, row: LocalRow): EntryState {
  if (row.pendingRemove === e.timestamp) return 'pendingRemove';
  if (row.pendingReactivate === e.timestamp) return 'pendingReactivate';
  if (e.entryType === 'Machine') return e.isDeactivated ? 'deactivatedMachine' : 'activeMachine';
  return 'existingManual';
}

function formatDateTimeWithoutTz(ts: string): string {
  if (!ts) return '';
  return ts.replace('T', ' ').slice(0, 16);
}

function extractTimeWithoutTz(ts: string): string {
  if (!ts) return '';
  const tIdx = ts.indexOf('T');
  if (tIdx >= 0) {
    return ts.slice(tIdx + 1, tIdx + 9);
  }
  return ts.slice(0, 8);
}

export default function TimekeepingErrorCorrection() {
  const dispatch = useAppDispatch();
  const selectedUnit = useAppSelector((s) => s.auth.selectedUnit);
  const employeeList = useAppSelector((s) => s.employees.list);
  const uiFields = useAppSelector((s) => s.applicationSettings.uiFields);
  const dbKey = selectedUnit
    ? `cranberry-employees-${selectedUnit.tenant}-${selectedUnit.solution}-${selectedUnit.unit}`
    : '';

  const employeeRecords = useEmployeeRecords(dbKey);

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [filterShiftValue, setFilterShiftValue] = useState<string>(''); // "shift:id" or "group:id"
  const [showRunning, setShowRunning] = useState<boolean>(false);
  const [filteredUUIDs, setFilteredUUIDs] = useState<string[] | null>(null);

  // Shifts and ShiftGroups data for filter dropdown
  const [shifts, setShifts] = useState<{ id: string; name: string }[]>([]);
  const [shiftGroups, setShiftGroups] = useState<{ id: string; name: string }[]>([]);

  // Rows and pagination
  const [rows, setRows] = useState<LocalRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(200);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Add Dialog State
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [activeAddRow, setActiveAddRow] = useState<LocalRow | null>(null);
  const [addDateInput, setAddDateInput] = useState<Dayjs | null>(null);
  const [addTimeInput, setAddTimeInput] = useState<Dayjs | null>(null);

  // ── Panel resize ─────────────────────────────────────────────────────────────
  const MIN_PANEL_WIDTH = 450;
  const [panelWidth, setPanelWidth] = useState(MIN_PANEL_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const handleResizeStart = (e: React.MouseEvent) => {
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelWidth;
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const newWidth = Math.max(MIN_PANEL_WIDTH, dragStartWidth.current + (e.clientX - dragStartX.current));
      setPanelWidth(newWidth);
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  // ── Load Shifts/Groups on Mount ─────────────────────────────────────────────
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const shiftsRes = await apiService.listShifts();
        setShifts((shiftsRes.data ?? []).map(s => ({ id: s.shiftId, name: s.shiftName })));
        
        const groupsRes = await apiService.listShiftGroups();
        setShiftGroups((groupsRes.data ?? []).map(g => ({ id: g.shiftGroupId, name: g.shiftGroupName })));
      } catch (err) {
        console.error('Failed to load shifts/groups', err);
      }
    };
    loadFilters();
  }, []);

  // ── Fetch Error Entries ──────────────────────────────────────────────────────
  const fetchErrors = useCallback(async (date: Dayjs, uuids: string[] | null, pageNum: number, currentSize: number) => {
    if (!date || !date.isValid()) return;

    const candidates = uuids
      ? employeeList.filter((e) => uuids.includes(e.employeeUUID))
      : employeeList;

    const withBio = candidates.filter((e) => e.biometricId);
    if (withBio.length === 0) {
      setRows([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    try {
      let shiftId: string | undefined;
      let shiftGroupId: string | undefined;

      if (filterShiftValue.startsWith('shift:')) {
        shiftId = filterShiftValue.substring(6);
      } else if (filterShiftValue.startsWith('group:')) {
        shiftGroupId = filterShiftValue.substring(6);
      }

      const res = await apiService.queryTimekeepingErrors({
        date: date.format('YYYY-MM-DD'),
        shiftId,
        shiftGroupId,
        showRunning,
        employees: withBio.map((e) => ({
          employeeInternalId: e.employeeInternalId,
          biometricId: e.biometricId,
          employeeName: e.employeeName,
        })),
        page: pageNum,
        pageSize: currentSize,
      });

      const responseData = res.data?.data ?? [];
      const total = res.data?.totalCount ?? 0;

      const newRows: LocalRow[] = responseData.map((item) => {
        const localItem: LocalRow = {
          ...item,
          entries: item.entries.map((e) => ({
            ...e,
            state: 'existingManual', // placeholder initially
          })),
        };
        // resolve entry states
        localItem.entries = localItem.entries.map((e) => ({
          ...e,
          state: getEntryState(e, localItem),
        }));
        return localItem;
      });

      setRows(newRows);
      setTotalCount(total);
    } catch (err: any) {
      dispatch(showNotification({ message: err?.message ?? 'Failed to load error entries', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [employeeList, filterShiftValue, showRunning, dispatch]);

  const handleFilterChange = (uuids: string[] | null) => {
    setFilteredUUIDs(uuids);
  };

  const handleSearch = () => {
    if (!selectedDate?.isValid()) return;
    setPage(0);
    fetchErrors(selectedDate, filteredUUIDs, 0, pageSize);
  };

  const handlePageChange = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
    if (selectedDate) {
      fetchErrors(selectedDate, filteredUUIDs, newPage, pageSize);
    }
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const size = parseInt(event.target.value, 10);
    setPageSize(size);
    setPage(0);
    if (selectedDate) {
      fetchErrors(selectedDate, filteredUUIDs, 0, size);
    }
  };

  // ── Local Actions ────────────────────────────────────────────────────────────

  const handleRemoveClick = (rowIdx: number, entry: DisplayEntry) => {
    setRows((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIdx) return row;

        let pendingAdd = row.pendingAdd;
        let pendingRemove = row.pendingRemove;
        let pendingReactivate = row.pendingReactivate;

        // If it's a locally added punch, just remove it
        if (entry.state === 'pendingAdd') {
          pendingAdd = undefined;
        } else if (entry.state === 'pendingReactivate') {
          // If we were reactivating, cancel it
          pendingReactivate = undefined;
        } else {
          // Remove/deactivate existing entry
          pendingRemove = entry.timestamp;
        }

        const updatedRow: LocalRow = {
          ...row,
          pendingAdd,
          pendingRemove,
          pendingReactivate,
          isDirty: !!(pendingAdd || pendingRemove || pendingReactivate),
        };

        updatedRow.entries = updatedRow.entries.map((e) => ({
          ...e,
          state: getEntryState(e, updatedRow),
        }));

        return updatedRow;
      })
    );
  };

  const handleReactivateClick = (rowIdx: number, entry: DisplayEntry) => {
    setRows((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIdx) return row;

        const updatedRow: LocalRow = {
          ...row,
          pendingReactivate: entry.timestamp,
          pendingRemove: undefined, // reactivating overrides removal
          isDirty: true,
        };

        updatedRow.entries = updatedRow.entries.map((e) => ({
          ...e,
          state: getEntryState(e, updatedRow),
        }));

        return updatedRow;
      })
    );
  };

  const openAddPunchDialog = (row: LocalRow) => {
    setActiveAddRow(row);
    // Default to the start time date without timezone conversion
    const startDateStr = row.shiftStart ? row.shiftStart.slice(0, 10) : '';
    setAddDateInput(startDateStr ? dayjs(startDateStr) : null);
    setAddTimeInput(null);
    setAddDialogOpen(true);
  };

  const handleAddConfirm = () => {
    if (!activeAddRow || !addDateInput?.isValid() || !addTimeInput?.isValid()) return;

    // Combine Date & Time inputs without browser timezone conversion
    const timeStr = addTimeInput.format('HH:mm:ss');
    const dateStr = addDateInput.format('YYYY-MM-DD');

    // Preserve shift timezone offset (e.g. "+06:00" or "Z") if present
    const match = activeAddRow.shiftStart.match(/([+-]\d{2}:\d{2}|Z)$/);
    const offset = match ? match[1] : '';
    const rfcStr = `${dateStr}T${timeStr}${offset}`;

    setRows((prev) =>
      prev.map((row) => {
        if (row.employeeInternalId !== activeAddRow.employeeInternalId || row.shiftId !== activeAddRow.shiftId) return row;

        const updatedRow: LocalRow = {
          ...row,
          pendingAdd: rfcStr,
          pendingRemove: undefined,
          pendingReactivate: undefined,
          isDirty: true,
        };

        // Update list of entries to display
        const displayEntries = row.entries.map((e) => ({
          ...e,
          state: getEntryState(e, updatedRow),
        }));

        updatedRow.entries = displayEntries;
        return updatedRow;
      })
    );

    setAddDialogOpen(false);
    setActiveAddRow(null);
  };

  // ── Save Corrections ──────────────────────────────────────────────────────────
  const isDirty = rows.some((r) => r.isDirty);

  const handleSave = async () => {
    const dirtyRows = rows.filter((r) => r.isDirty);
    if (dirtyRows.length === 0) return;

    setSaving(true);
    let successCount = 0;
    try {
      for (const row of dirtyRows) {
        await apiService.saveTimekeepingError({
          employeeInternalId: row.employeeInternalId,
          biometricId: row.biometricId,
          add: row.pendingAdd,
          remove: row.pendingRemove,
          reactivate: row.pendingReactivate,
        });
        successCount++;
      }

      dispatch(
        showNotification({
          message: `Saved corrections for ${successCount} employee(s).`,
          severity: 'success',
        })
      );
      if (selectedDate) {
        fetchErrors(selectedDate, filteredUUIDs, page, pageSize);
      }
    } catch (err: any) {
      dispatch(
        showNotification({
          message: err?.message ?? 'Failed to save some corrections. Please try again.',
          severity: 'error',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (selectedDate) {
      fetchErrors(selectedDate, filteredUUIDs, page, pageSize);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ display: 'flex', m: -3, minHeight: 'calc(100vh - 64px)' }}>
        {/* Left Filter Panel */}
        <Box
          sx={{
            width: panelWidth,
            minWidth: MIN_PANEL_WIDTH,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2.5}>
            Filter Employees
          </Typography>
          {uiFields.length > 0 && (
            <EmployeeFilter
              uiFields={uiFields}
              employees={employeeRecords}
              onChange={handleFilterChange}
              onSearch={handleSearch}
              searchDisabled={!selectedDate?.isValid() || !filteredUUIDs}
            />
          )}
        </Box>

        {/* Drag handle for resizing left panel */}
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            width: '4px',
            cursor: 'col-resize',
            bgcolor: isDragging ? 'primary.main' : 'transparent',
            transition: 'background-color 0.2s',
            '&:hover': { bgcolor: 'primary.light' },
          }}
        />

        {/* Right Main Grid Panel */}
        <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
          {/* Top Filter Bar */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <DatePicker
                label="Date"
                value={selectedDate}
                onChange={setSelectedDate}
                slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
              />

              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Shift / Shift Group</InputLabel>
                <Select
                  value={filterShiftValue}
                  onChange={(e) => setFilterShiftValue(e.target.value)}
                  label="Shift / Shift Group"
                >
                  <MenuItem value="">
                    <em>All Shifts</em>
                  </MenuItem>
                  {shifts.map((s) => (
                    <MenuItem key={`shift:${s.id}`} value={`shift:${s.id}`}>
                      Shift: {s.name}
                    </MenuItem>
                  ))}
                  {shiftGroups.map((g) => (
                    <MenuItem key={`group:${g.id}`} value={`group:${g.id}`}>
                      Group: {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={showRunning}
                    onChange={(e) => setShowRunning(e.target.checked)}
                    color="primary"
                  />
                }
                label="Show running shifts"
              />

              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                sx={{ ml: 'auto', px: 3 }}
              >
                Search
              </Button>
            </Box>
          </Paper>

          {/* Error Entries Grid */}
          <TableContainer component={Paper} variant="outlined" sx={{ flexGrow: 1, maxHeight: 'calc(100vh - 350px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Internal ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Biometric ID</TableCell>
                  <TableCell>Shift Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Shift Range</TableCell>
                  <TableCell>Punches (Shift Window)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={30} />
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Loading errors…
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        No timekeeping errors found for the selected date/shift.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, rowIdx) => {
                    const hasAction = !!(row.pendingAdd || row.pendingRemove || row.pendingReactivate);
                    return (
                      <TableRow key={`${row.employeeInternalId}-${row.shiftId}`} hover>
                        <TableCell>{row.employeeInternalId}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{row.employeeName}</TableCell>
                        <TableCell>{row.biometricId}</TableCell>
                        <TableCell>{row.shiftName}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.shiftStatus}
                            size="small"
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              bgcolor: row.shiftStatus === 'Running' ? '#fff8e1' : '#f5f5f5',
                              color: row.shiftStatus === 'Running' ? '#e65100' : 'text.secondary',
                              border: '1px solid',
                              borderColor: row.shiftStatus === 'Running' ? '#ffe082' : '#e0e0e0',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {formatDateTimeWithoutTz(row.shiftStart)} to{' '}
                          {formatDateTimeWithoutTz(row.shiftEnd)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            {row.entries.map((entry) => {
                              const c = COLOR[entry.state];
                              const isDeactivated = entry.isDeactivated || entry.state === 'pendingRemove';
                              return (
                                <Chip
                                  key={entry.timestamp}
                                  label={`${entry.time} (${entry.entryType})`}
                                  size="small"
                                  onDelete={
                                    !hasAction || entry.state === 'pendingRemove' || entry.state === 'pendingReactivate'
                                      ? () => handleRemoveClick(rowIdx, entry)
                                      : undefined
                                  }
                                  onClick={
                                    entry.state === 'deactivatedMachine' && !hasAction
                                      ? () => handleReactivateClick(rowIdx, entry)
                                      : undefined
                                  }
                                  sx={{
                                    bgcolor: c.bg,
                                    color: c.text,
                                    border: '1px solid',
                                    borderColor: c.border,
                                    textDecoration: isDeactivated ? 'line-through' : 'none',
                                    opacity: isDeactivated ? 0.75 : 1,
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    height: 24,
                                    '& .MuiChip-deleteIcon': {
                                      color: 'rgba(255, 255, 255, 0.7)',
                                      '&:hover': { color: '#ffffff' },
                                    },
                                  }}
                                />
                              );
                            })}

                            {/* Show pending add chip if exists */}
                            {row.pendingAdd && (
                              <Chip
                                label={`${extractTimeWithoutTz(row.pendingAdd)} (Add)`}
                                size="small"
                                onDelete={() => handleRemoveClick(rowIdx, { state: 'pendingAdd', timestamp: '' } as DisplayEntry)}
                                sx={{
                                  bgcolor: COLOR.pendingAdd.bg,
                                  color: COLOR.pendingAdd.text,
                                  border: '1px dashed',
                                  borderColor: COLOR.pendingAdd.border,
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                  height: 24,
                                  '& .MuiChip-deleteIcon': {
                                    color: 'rgba(230, 81, 0, 0.7)',
                                    '&:hover': { color: '#e65100' },
                                  },
                                }}
                              />
                            )}

                            {/* Add punch button (only visible if no correction is pending on this row) */}
                            {!hasAction && (
                              <Tooltip title="Add manual punch within shift boundaries">
                                <IconButton
                                  size="small"
                                  onClick={() => openAddPunchDialog(row)}
                                  sx={{ border: '1.5px dashed #ccc', p: '2px', color: 'text.secondary' }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handlePageSizeChange}
            rowsPerPageOptions={[50, 100, 200, 500]}
          />
        </Box>
      </Box>

      {/* Sticky Action Footer */}
      <Paper
        elevation={6}
        sx={{
          position: 'sticky',
          bottom: 0,
          px: 3,
          py: 1.5,
          display: 'flex',
          gap: 1.5,
          borderRadius: 0,
          mt: 'auto',
          zIndex: 100,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || !isDirty}
          sx={{ px: 4 }}
        >
          {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Save Corrections
        </Button>
        <Button variant="outlined" onClick={handleReset} disabled={loading || saving || !isDirty}>
          Reset
        </Button>
      </Paper>

      {/* Add Punch Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Timekeeping Entry</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}>
          {activeAddRow && (
            <>
              <Alert severity="info" sx={{ py: '2px', px: '10px' }}>
                Proposed entry must fall strictly inside shift limits:
                <br />
                <strong>{formatDateTimeWithoutTz(activeAddRow.shiftStart)}</strong> to{' '}
                <strong>{formatDateTimeWithoutTz(activeAddRow.shiftEnd)}</strong>
              </Alert>

              <DatePicker
                label="Punch Date"
                value={addDateInput}
                onChange={setAddDateInput}
                minDate={activeAddRow.shiftStart ? dayjs(activeAddRow.shiftStart.slice(0, 10)) : undefined}
                maxDate={activeAddRow.shiftEnd ? dayjs(activeAddRow.shiftEnd.slice(0, 10)) : undefined}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />

              <TimePicker
                label="Punch Time"
                value={addTimeInput}
                onChange={setAddTimeInput}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAddDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleAddConfirm}
            variant="contained"
            disabled={!addDateInput?.isValid() || !addTimeInput?.isValid()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
